import { DashboardFiltersPayload } from '../../services/dashboard.service';
import {
  CreativeGalleryBase,
  CreativeGalleryDetailed,
  CreativeGallerySortField,
  CreativeGallerySort
} from '../my-brand-screen/types';
import { SelectItem } from '../../shared/components/select-menu/select-menu.component';
import { ATTENTION_METRIC_NAME } from '../../shared/types/common';

export interface MarketingResearchCreatives {
    id: string;
    imgUrl: string;
}

export interface MarketingResearchGroupingCreative extends MarketingResearchCreatives {
    attention: number;
    viewability: number;
    cpm: number;
    ctr: number;
    timeInView: number;
    brandName: string;
    groupingType: GroupingTypeKey;
}

export interface MarketingResearchNumericMetric {
    spend: number;
    impressions: number;
    attention: number;
    ctr: number;
}

export interface MarketingResearchMetrics extends MarketingResearchNumericMetric {
    creatives: MarketingResearchCreatives[];
    creativeCount: number;
    impressionShare: number;
    lastSeen: string;
}

export interface MarketingResearchBrandCategory {
    brandKey: string;
    current: MarketingResearchMetrics;
    diff: MarketingResearchNumericMetric;
}

export interface MarketingResearchCategory {
    name: string;
    description?: string;
    brands: MarketingResearchBrandCategory[];
}

export interface MessagingResponse {
    categories: MarketingResearchCategory[];     
    total: number;
    groupingType:GroupingTypeKey;
}

export interface MessagingGroupingResponse {
    categories: MarketingResearchCategory[];     
    groupingType:GroupingTypeKey;
}

// Creative Gallery Types for Messaging
export interface MessagingCreativeGalleryPayload extends DashboardFiltersPayload {
  brandsKeys: string[];
  sortField?: CreativeGallerySortField;
  limit?: number;
  offset?: number;
  isAsc?: boolean;
  themes?: string[];
  minImpressions?: number;
}

export interface MessagingCreativeGalleryResponse {
  creativesData: CreativeGalleryBase[];
  total: number;
  themes: string[];
}

export interface MessagingCreativeDataPayload extends DashboardFiltersPayload {
  brandsKeys: string[];
  creativeId: string;
}

export type MessagingCreativeDataResponse = CreativeGalleryDetailed;

export interface MessagingCreativeGalleryFileResponse {
  fileUrl: string;
}

type MessagingGallerySortField = 'impressions' | 'ctr' | 'cpm';

const messagingSortFieldLabels: Record<MessagingGallerySortField, string> = {
  impressions: 'Impressions',
  ctr: 'CTR',
  cpm: 'CPM'
};

const messagingSortFields: MessagingGallerySortField[] = ['impressions', 'ctr', 'cpm'];

export const MESSAGING_GALLERY_SORT_OPTIONS: SelectItem<CreativeGallerySort>[] = messagingSortFields.flatMap((field) => [
  {
    key: { sortField: field, isAsc: false },
    label: `${messagingSortFieldLabels[field]} (Descending)`
  },
  {
    key: { sortField: field, isAsc: true },
    label: `${messagingSortFieldLabels[field]} (Ascending)`
  }
]);

export enum GalleryQueryParam {
  BRAND_KEY = 'brandKey',
  COMPETITOR = 'competitor',
  START_DATE = 'startDate',
  END_DATE = 'endDate',
  RELATIVE_DATE = 'relativeDate',
  COUNTRY = 'country',
  DEVICE = 'device',
  SORT_FIELD = 'sortField',
  IS_ASC = 'isAsc',
  THEME = 'theme',
  PAGE = 'page',
  LIMIT = 'limit'
}

export const MessagingMetric = {
  CREATIVES: 'Creatives',
  IMPRESSION_SHARE: 'Impression Share',
  SPEND: 'Spend',
  IMPRESSIONS: 'Impressions',
  ATTENTION: ATTENTION_METRIC_NAME,
  CTR: 'CTR',
  LAST_SEEN: 'Last Seen',
} as const;

export const MESSAGING_BRAND_HEADER_WIDTH = 166;


export const groupingTypes = {
  themes: 'Messaging Themes',
  attention: ATTENTION_METRIC_NAME,
  dwell_time: 'Dwell Time',
  ctr: 'CTR',
}

export const THEMES = "themes"
export type GroupingTypeKey = keyof typeof groupingTypes;
export type GroupingTypeLabel = typeof groupingTypes[GroupingTypeKey];

export const groupingTypesLabels = Object.values(groupingTypes);

export const groupingTypePosthogValues: Record<GroupingTypeKey, string> = {
  themes: 'messaging_themes',
  attention: 'tru_attention',
  dwell_time: 'dwell_time',
  ctr: 'ctr',
};